import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import CompanySettingsService from '../../../../../services/CompanySettingsService';
import formatCurrency from '../../../../../utils/formatCurrency';
import { Email, EmailsObjectType, KindOfVtSpTransType, MaxContestsAllowedType, PartnerType, SettingsFromApiI, SettingsListI, TechnologyType, TransfersBehaviorType, VT_PURCHASE_PROVIDER_NOT_INFORMED, VT_PURCHASE_PROVIDER_SAME_AS_SOURCE, VT_PURCHASE_PROVIDER_SAME_AS_SOURCE_LABEL, VtProviderType } from '../../types';

interface UseLoadSettingsI {
  setCopyGoingToReturn: Dispatch<SetStateAction<boolean>>;
  setHasAffreightment: Dispatch<SetStateAction<boolean>>;
  setUsaHorarioFretado: Dispatch<SetStateAction<boolean>>;
  setHasKmLimit: Dispatch<SetStateAction<boolean>>;
  setKmLimit: Dispatch<SetStateAction<number[]>>;
  setWalkDistance: Dispatch<SetStateAction<number[]>>;
  setSelectedKmLimit: Dispatch<SetStateAction<number[]>>;
  setSelectedWalkDistance: Dispatch<SetStateAction<number[]>>;
  setTransfersBehavior: Dispatch<SetStateAction<TransfersBehaviorType>>;
  setCompareWithDesiredVtValue: Dispatch<SetStateAction<boolean>>;
  setUtilizaTetoValorPadraoEmp: Dispatch<SetStateAction<boolean>>;
  setDesiredVtValue: Dispatch<SetStateAction<string>>;
  setChooseDefaultValueForRoutings: Dispatch<SetStateAction<boolean>>;
  setDefaultValueForRoutings: Dispatch<SetStateAction<string>>;
  setKindOfVtSpTrans: Dispatch<SetStateAction<KindOfVtSpTransType>>;
  setVtProvider: Dispatch<SetStateAction<VtProviderType>>;
  setVtProviderOptions: Dispatch<SetStateAction<VtProviderType[]>>;
  setVtPurchaseProvider: Dispatch<SetStateAction<VtProviderType>>;
  setHasCustomLogo: Dispatch<SetStateAction<boolean>>;
  setShowTicketsAtNotOptimizedResults: Dispatch<SetStateAction<boolean>>;
  setHasCustomTextAtLetter: Dispatch<SetStateAction<boolean>>;
  setCustomTextAtLetter: Dispatch<SetStateAction<string>>;
  setPartner: Dispatch<SetStateAction<PartnerType>>;
  setPartnerOptions: Dispatch<SetStateAction<PartnerType[]>>;
  setSecondPartner: Dispatch<SetStateAction<PartnerType>>;
  setDirectResult: Dispatch<SetStateAction<boolean>>;
  setMaxContestsAllowed: Dispatch<SetStateAction<MaxContestsAllowedType>>;
  setSendAnswerOfClaimToCollabEmail: Dispatch<SetStateAction<boolean>>;
  setAllowCollabToSeeLetter: Dispatch<SetStateAction<boolean>>;
  setAllowMultipleConsults: Dispatch<SetStateAction<boolean>>;
  setUsesCustomLogo: Dispatch<SetStateAction<boolean>>;
  setIntegrateWithWs: Dispatch<SetStateAction<boolean>>;
  setLosesOwnershipWithoutBoarding: Dispatch<SetStateAction<boolean>>;
  setDaysAllowedWithoutBoarding: Dispatch<SetStateAction<number | string>>;
  setWillIntegrateWithPayroll: Dispatch<SetStateAction<boolean>>;
  setTechnology: Dispatch<SetStateAction<TechnologyType>>;
  setTechnologyOptions: Dispatch<SetStateAction<TechnologyType[]>>;
  setRoutingNoResultEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setOriginalRoutingNoResultEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setRoutingEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setOriginalRoutingEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setExtractEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setOriginalExtractEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setVtItensRelationsRequestEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setOriginalVtItensRelationsRequestEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setFinancialEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setOriginalFinancialEmails: Dispatch<SetStateAction<EmailsObjectType[]>>;
  setImageFile: Dispatch<SetStateAction<File | string | undefined>>;
  setIsMyLinkFull: Dispatch<SetStateAction<boolean>>;
  setDefaultWorkplaceUsesIntegrationWs: Dispatch<SetStateAction<boolean>>;
  setChangesWpDataByWs: Dispatch<SetStateAction<boolean>>;
  setIsPartner: Dispatch<SetStateAction<boolean>>;
  setUsesLogoAtEmail: Dispatch<SetStateAction<boolean>>;
  setVerifySalaryRJ: Dispatch<SetStateAction<boolean>>;
  setCanRequestVtItemRelation: Dispatch<SetStateAction<boolean>>;
  setHasWebhook: Dispatch<SetStateAction<boolean>>;
  setKindOfVtAesa: Dispatch<SetStateAction<string>>;
  setAboveDesiredValueReturnsBiasTag: Dispatch<SetStateAction<boolean>>;
  setHasResultReadyWebhook: Dispatch<SetStateAction<boolean>>;
  setHasResultOutOfScopeWebhook: Dispatch<SetStateAction<boolean>>;
  setIntegrator: Dispatch<SetStateAction<PartnerType>>;
  setParticularities: Dispatch<SetStateAction<string>>;
  setMustSendAddressProof: Dispatch<SetStateAction<boolean>>;
  setUsaRodoviarioValZero: Dispatch<SetStateAction<boolean>>;
  setKmRodoviarioValZero: Dispatch<SetStateAction<number | string>>;
  setGeraNFSeparada: Dispatch<SetStateAction<boolean>>;
}

export default function useLoadSettings({
  setCopyGoingToReturn,
  setHasAffreightment,
  setUsaHorarioFretado,
  setHasKmLimit,
  setKmLimit,
  setWalkDistance,
  setTransfersBehavior,
  setCompareWithDesiredVtValue,
  setUtilizaTetoValorPadraoEmp,
  setDesiredVtValue,
  setChooseDefaultValueForRoutings,
  setDefaultValueForRoutings,
  setKindOfVtSpTrans,
  setVtProvider,
  setVtProviderOptions,
  setVtPurchaseProvider,
  setHasCustomLogo,
  setShowTicketsAtNotOptimizedResults,
  setHasCustomTextAtLetter,
  setCustomTextAtLetter,
  setPartner,
  setPartnerOptions,
  setSecondPartner,
  setDirectResult,
  setMaxContestsAllowed,
  setSendAnswerOfClaimToCollabEmail,
  setAllowCollabToSeeLetter,
  setAllowMultipleConsults,
  setUsesCustomLogo,
  setIntegrateWithWs,
  setLosesOwnershipWithoutBoarding,
  setDaysAllowedWithoutBoarding,
  setWillIntegrateWithPayroll,
  setTechnology,
  setTechnologyOptions,
  setRoutingNoResultEmails,
  setOriginalRoutingNoResultEmails,
  setRoutingEmails,
  setOriginalRoutingEmails,
  setVtItensRelationsRequestEmails,
  setOriginalVtItensRelationsRequestEmails,
  setFinancialEmails,
  setOriginalFinancialEmails,
  setExtractEmails,
  setOriginalExtractEmails,
  setSelectedKmLimit,
  setSelectedWalkDistance,
  setImageFile,
  setIsMyLinkFull,
  setDefaultWorkplaceUsesIntegrationWs,
  setChangesWpDataByWs,
  setIsPartner,
  setUsesLogoAtEmail,
  setVerifySalaryRJ,
  setCanRequestVtItemRelation,
  setHasWebhook,
  setKindOfVtAesa,
  setAboveDesiredValueReturnsBiasTag,
  setHasResultReadyWebhook,
  setHasResultOutOfScopeWebhook,
  setIntegrator,
  setParticularities,
  setMustSendAddressProof,
  setUsaRodoviarioValZero,
  setKmRodoviarioValZero,
  setGeraNFSeparada,
}: UseLoadSettingsI) {
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const [tabsDisabled, setTabsDisabled] = useState<string[]>([]);
  const [currentServices, setCurrentServices] = useState<{ codServ: number; servico: string }[]>([]);
  const [geraNFSeparadaMatriz, setGeraNFSeparadaMatriz] = useState(false);

  const { companyCode, userCode, userProfile } = useGetGeneralAppData();
  const { apiCall } = useApiCall();
  const navigate = useNavigate();

  const loadCompanySettings = useCallback(async () => {
    await apiCall({
      apiToCall: CompanySettingsService.getCompanySettings,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      catchMessage: 'Houve um erro ao carregar os dados de configuração da empresa',
      catchAction: () => navigate('/company'),
      actionAfterResponse: (response: SettingsFromApiI) => {
        if (!response.codigo) {
          toast.error('Ocorreu um erro ao carregar os dados de configuração da empresa');
          navigate('/company');
          return;
        }

        if (!response.codRetorno) {
          toast.info('Essa empresa ainda não teve suas configurações definidas, por favor, defina-as. Os campos foram preenchidos de acordo com o padrão.');
          setIsFirstSetup(true);
          return;
        }
        setIsFirstSetup(false);

        const { emails, dadosConfig, servicosContrato } = response;

        const extractConsultEmails = emails?.filter((emailObj) => emailObj?.codProcesso === 1);
        const routingAccepteedEmails = emails?.filter((emailObj) => emailObj?.codProcesso === 2);
        const routingNoResultEmails = emails?.filter((emailObj) => emailObj?.codProcesso === 3);
        const vtItensRelationsRequestEmails = emails?.filter((emailObj) => emailObj?.codProcesso === 4);
        const financialEmails = emails?.filter((emailObj) => emailObj?.codProcesso === 5);

        const mapEmails = (emailObj: Email[]) => (emailObj?.map((emailObj) => ({ email: emailObj.endMail, emailOwnerName: emailObj.nomeUsuario })) || []);

        setExtractEmails(mapEmails(extractConsultEmails));
        setOriginalExtractEmails(mapEmails(extractConsultEmails));
        setRoutingEmails(mapEmails(routingAccepteedEmails));
        setOriginalRoutingEmails(mapEmails(routingAccepteedEmails));
        setRoutingNoResultEmails(mapEmails(routingNoResultEmails));
        setOriginalRoutingNoResultEmails(mapEmails(routingNoResultEmails));
        setVtItensRelationsRequestEmails(mapEmails(vtItensRelationsRequestEmails));
        setOriginalVtItensRelationsRequestEmails(mapEmails(vtItensRelationsRequestEmails));
        setFinancialEmails(mapEmails(financialEmails));
        setOriginalFinancialEmails(mapEmails(financialEmails));

        setCopyGoingToReturn(dadosConfig.espelhamento);
        setHasAffreightment(dadosConfig.usaFreta);
        setUsaHorarioFretado(dadosConfig.usaHorarioFretado ?? true);
        setHasKmLimit(dadosConfig.usaLimKm);
        setKmLimit([5, dadosConfig.kmLimite < 5 ? 5 : (dadosConfig.kmLimite > 1000 ? dadosConfig.kmLimite / 1000 : dadosConfig.kmLimite)]);
        setSelectedKmLimit([5, dadosConfig.kmLimite < 5 ? 5 : (dadosConfig.kmLimite > 1000 ? dadosConfig.kmLimite / 1000 : dadosConfig.kmLimite)]);
        setWalkDistance([500, dadosConfig.distInicial < 500 ? 500 : dadosConfig.distInicial]);
        setSelectedWalkDistance([500, dadosConfig.distInicial < 500 ? 500 : dadosConfig.distInicial]);

        const transfersbehaviorMap: {[key: number]: TransfersBehaviorType} = {
          0: 'unlimited',
          1: 'minimun',
          2: 'default',
        };
        setTransfersBehavior(transfersbehaviorMap[dadosConfig.tipoBaldeacao]);
        setCompareWithDesiredVtValue(dadosConfig.utilizaTeto);
        setDesiredVtValue(formatCurrency(dadosConfig.valorTeto.toFixed(2)));
        setUtilizaTetoValorPadraoEmp(dadosConfig.utilizaTetoValorPadraoEmp ?? false);
        setAboveDesiredValueReturnsBiasTag(dadosConfig.acimaTetoBiasTag);
        setChooseDefaultValueForRoutings(dadosConfig.usaVTInicial);
        setDefaultValueForRoutings(formatCurrency(dadosConfig.valVTInicial.toFixed(2)));
        setKindOfVtSpTrans(dadosConfig.regraVTBilSemTx ? 'common' : 'vt');
        setKindOfVtAesa(dadosConfig.regraVTSemTxAesa ? 'common' : 'vt');
        setVtProvider({ value: String(dadosConfig.envelopadora), label: dadosConfig.nomeEnvelopadora ?? 'Selecione um fornecedor' });

        const envelopadoraFornecedor = dadosConfig.envelopadoraFornecedor ?? 0;
        if (!dadosConfig.envelopadora || dadosConfig.envelopadora === 0) {
          setVtPurchaseProvider({ value: VT_PURCHASE_PROVIDER_NOT_INFORMED, label: 'Não informado' });
        } else if (!envelopadoraFornecedor) {
          setVtPurchaseProvider({ value: VT_PURCHASE_PROVIDER_NOT_INFORMED, label: 'Não informado' });
        } else if (String(envelopadoraFornecedor) === String(dadosConfig.envelopadora)) {
          setVtPurchaseProvider({
            value: VT_PURCHASE_PROVIDER_SAME_AS_SOURCE,
            label: VT_PURCHASE_PROVIDER_SAME_AS_SOURCE_LABEL,
          });
        } else {
          setVtPurchaseProvider({
            value: String(envelopadoraFornecedor),
            label: dadosConfig.nomeEnvelopadoraFornecedor ?? 'Selecione um fornecedor',
          });
        }

        setHasCustomLogo(dadosConfig.logoCarta);
        setShowTicketsAtNotOptimizedResults(!dadosConfig.cartaEmBranco);
        setHasCustomTextAtLetter(!!dadosConfig.opcaoTextoCarta);
        setCustomTextAtLetter(dadosConfig.textoCarta);
        setPartner({ value: String(dadosConfig.codParceiro), label: dadosConfig.nomeParceiro ?? 'Nenhum parceiro' });
        setSecondPartner({
          value: dadosConfig.codParceiro2 ? String(dadosConfig.codParceiro2) : null,
          label: dadosConfig.nomeParceiro2 || 'Nenhum parceiro',
        });
        setIntegrator({ value: String(dadosConfig.codIntegrador), label: dadosConfig.nomeIntegrador ?? 'Nenhum integrador' });
        setDirectResult(dadosConfig.notificaFuncDirect);
        setMaxContestsAllowed(dadosConfig.totalMaxReclama);
        setSendAnswerOfClaimToCollabEmail(dadosConfig.emailFuncWebC);
        setAllowCollabToSeeLetter(dadosConfig.cartaLiberada);
        setAllowMultipleConsults(dadosConfig.reprocessaMyLink);
        setUsesCustomLogo(dadosConfig.MyLinkLogo);
        setIntegrateWithWs(dadosConfig.envioDadosWCAceite);
        setLosesOwnershipWithoutBoarding(dadosConfig.bloqueiaEmbCAF);
        setDaysAllowedWithoutBoarding(dadosConfig.diasBloqueioCAF);
        setWillIntegrateWithPayroll(dadosConfig.conexaoFolhaPg);
        setTechnology({ value: String(dadosConfig.codTecFolhaPg), label: dadosConfig.nomeTecFolhaPg ?? 'Nenhuma tecnologia' });
        setIsMyLinkFull(dadosConfig.usaMyLinkFull);
        setDefaultWorkplaceUsesIntegrationWs(!(dadosConfig.regraBilSemIntegraRJ));
        setChangesWpDataByWs(dadosConfig.alteraDadosLT);

        setImageFile(dadosConfig.urlArqLogo);
        setCurrentServices(servicosContrato ?? []);
        setIsPartner(dadosConfig.tipoParceiro);
        setUsesLogoAtEmail(dadosConfig.temLogoEmail);

        setVerifySalaryRJ(dadosConfig.verificaSalarioRJ);

        setCanRequestVtItemRelation(dadosConfig.atualizaCodVTEnvelp);
        setHasWebhook(dadosConfig.usaWebhook);
        setHasResultReadyWebhook(dadosConfig.usaWebhook2);
        setHasResultOutOfScopeWebhook(dadosConfig.usaWebhookForaAbrang);
        setParticularities(dadosConfig.particularidades);
        setMustSendAddressProof(dadosConfig.anexarComprovanteEnd);
        setUsaRodoviarioValZero(dadosConfig.usaRodoviarioValZero ?? false);
        setKmRodoviarioValZero(dadosConfig.kmRodoviarioValZero ? dadosConfig.kmRodoviarioValZero / 1000 : 0);
        setGeraNFSeparada(dadosConfig.geraNFSeparada ?? false);
        setGeraNFSeparadaMatriz(dadosConfig.geraNFSeparadaMatriz ?? false);

        const tabsToDisable: string[] = [];
        const hasCod15 = servicosContrato?.some(service => service.codServ === 15);
        const hasCod14 = servicosContrato?.some(service => service.codServ === 14);

        if (!hasCod15) {
          tabsToDisable.push('myLink');
        }

        if (!hasCod14) {
          tabsToDisable.push('accessControlAffreightment');
        }

        setTabsDisabled(tabsToDisable);
      },
    });
  }, [apiCall, companyCode, navigate, setAboveDesiredValueReturnsBiasTag, setAllowCollabToSeeLetter, setAllowMultipleConsults, setCanRequestVtItemRelation, setChangesWpDataByWs, setChooseDefaultValueForRoutings, setCompareWithDesiredVtValue, setCopyGoingToReturn, setCustomTextAtLetter, setDaysAllowedWithoutBoarding, setDefaultValueForRoutings, setDefaultWorkplaceUsesIntegrationWs, setDesiredVtValue, setDirectResult, setExtractEmails, setFinancialEmails, setGeraNFSeparada, setHasAffreightment, setUsaHorarioFretado, setHasCustomLogo, setHasCustomTextAtLetter, setHasKmLimit, setHasResultOutOfScopeWebhook, setHasResultReadyWebhook, setHasWebhook, setImageFile, setIntegrateWithWs, setIntegrator, setIsMyLinkFull, setIsPartner, setKindOfVtAesa, setKindOfVtSpTrans, setKmLimit, setKmRodoviarioValZero, setLosesOwnershipWithoutBoarding, setMaxContestsAllowed, setMustSendAddressProof, setOriginalExtractEmails, setOriginalFinancialEmails, setOriginalRoutingEmails, setOriginalRoutingNoResultEmails, setOriginalVtItensRelationsRequestEmails, setParticularities, setPartner, setSecondPartner, setRoutingEmails, setRoutingNoResultEmails, setSelectedKmLimit, setSelectedWalkDistance, setSendAnswerOfClaimToCollabEmail, setShowTicketsAtNotOptimizedResults, setTechnology, setTransfersBehavior, setUsaRodoviarioValZero, setUsesCustomLogo, setUsesLogoAtEmail, setUtilizaTetoValorPadraoEmp, setVerifySalaryRJ, setVtItensRelationsRequestEmails, setVtProvider, setVtPurchaseProvider, setWalkDistance, setWillIntegrateWithPayroll, userCode, userProfile]);

  const loadLists = useCallback(async () => {
    await apiCall({
      apiToCall: CompanySettingsService.getListsToSetupCompany,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      catchMessage: 'Houve um erro ao carregar os dados de configuração da empresa (falha ao recuperar listas)',
      catchAction: () => navigate('/company'),
      actionAfterResponse: (response: SettingsListI) => {
        if (!response.codigo) {
          toast.error('Ocorreu um erro ao carregar os dados de configuração da empresa');
          navigate('/company');
          return;
        }
        const providerOptions = response.envelopadoras?.map((pvdObj) => ({ value: String(pvdObj.codEnvelop), label: pvdObj.nomeEnvelop }));
        const partnerOptions = response.parceiros?.map((prtObj) => ({ value: String(prtObj.codParceiro), label: prtObj.nomeParceiro, codEmpresaParceiro: prtObj.codEmpresaParceiro }));
        const technologyOptions = response.tecnoFolhaPg?.map((tchObj) => ({ value: String(tchObj.codTecFolhaPg), label: tchObj.nomeTecFolhaPG }));
        setVtProviderOptions([{ value: null, label: 'Selecione um fornecedor' }, ...providerOptions]);
        setPartnerOptions([{ value: null, label: 'Nenhum parceiro' }, ...partnerOptions]);
        setTechnologyOptions([{ value: null, label: 'Nenhuma tecnologia' }, ...technologyOptions]);
      },
    });
  }, [apiCall, companyCode, navigate, setPartnerOptions, setTechnologyOptions, setVtProviderOptions, userCode, userProfile]);

  const loadPage = useCallback(async () => {
    setIsLoadLoading(true);
    await Promise.all([loadCompanySettings(), loadLists()]);
    setIsLoadLoading(false);
  }, [loadCompanySettings, loadLists]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  return {
    isLoadLoading,
    loadPage,
    isFirstSetup,
    tabsDisabled,
    currentServices,
    geraNFSeparadaMatriz,
  };
}
